'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { DOC_TYPE_ORDER, TravellerDocDetail, TravellerDocType } from '@/src/types/traveller'
import useFormatDate from '@/src/hooks/date/useFormatDate'

export interface TravellerDocTabsVm {
    activeDocType: TravellerDocType | null
    activeDoc: TravellerDocDetail | null
    activeExpiryDateValue: string
    onTabChange: (nextDocType: TravellerDocType) => void
}

export function useTravellerDocTabs(documents: TravellerDocDetail[]): TravellerDocTabsVm {
    const { formatDate } = useFormatDate()
    const availableDocTypes = useMemo(() => {
        const present = new Set(documents.map(doc => doc.docType))
        return DOC_TYPE_ORDER.filter(type => present.has(type))
    }, [documents])

    const initialDocType = useMemo(() => availableDocTypes[0] || null, [availableDocTypes])

    const [activeDocType, setActiveDocType] = useState<TravellerDocType | null>(initialDocType)

    useEffect(() => {
        // Keep the first available tab active by default when documents change.
        if (!initialDocType) {
            setActiveDocType(null)
            return
        }

        setActiveDocType(initialDocType)
    }, [initialDocType])

    const activeDoc = useMemo(() => {
        if (!activeDocType) return null
        return documents.find(doc => doc.docType === activeDocType) || null
    }, [documents, activeDocType])

    const activeExpiryDateValue = useMemo(() => {
        if (!activeDoc?.expiryDate) return '--'
        return formatDate(activeDoc.expiryDate, 'DD MMMM YYYY')
    }, [activeDoc, formatDate])

    const onTabChange = useCallback((nextDocType: TravellerDocType) => {
        setActiveDocType(nextDocType)
    }, [])

    return {
        activeDocType,
        activeDoc,
        activeExpiryDateValue,
        onTabChange
    }
}

export default useTravellerDocTabs

